/**
 * Performance Diagnostics Overlay
 *
 * Displays real-time performance metrics on screen for mobile debugging
 */

export class PerformanceDiagnostics {
  constructor() {
    this.events = [];
    this.maxEvents = 10;
    this.overlay = null;
    this.isEnabled = false;
  }

  /**
   * Initialize and show diagnostics overlay
   */
  init() {
    this.isEnabled = true;

    // Create overlay UI
    this.overlay = document.createElement('div');
    this.overlay.id = 'perf-diagnostics';
    this.overlay.style.cssText = `
      position: fixed;
      top: 10px;
      left: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.85);
      color: #0f0;
      font-family: monospace;
      font-size: 11px;
      padding: 10px;
      z-index: 99999;
      max-height: 400px;
      overflow-y: auto;
      border: 2px solid #0f0;
      border-radius: 5px;
      pointer-events: none;
    `;

    // Add save button
    const saveBtn = document.createElement('button');
    saveBtn.textContent = '💾';
    saveBtn.style.cssText = `
      position: absolute;
      top: 5px;
      right: 35px;
      background: #0a0;
      color: #fff;
      border: none;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      cursor: pointer;
      pointer-events: auto;
      font-size: 14px;
      font-weight: bold;
    `;
    saveBtn.onclick = () => this.saveToServer();
    saveBtn.title = 'Save diagnostics to server';
    this.overlay.appendChild(saveBtn);

    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = `
      position: absolute;
      top: 5px;
      right: 5px;
      background: #f00;
      color: #fff;
      border: none;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      cursor: pointer;
      pointer-events: auto;
      font-size: 14px;
      font-weight: bold;
    `;
    closeBtn.onclick = () => this.hide();
    this.overlay.appendChild(closeBtn);

    // Add title
    const title = document.createElement('div');
    title.textContent = '📊 Android Performance Diagnostics';
    title.style.cssText = 'font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #ff0;';
    this.overlay.appendChild(title);

    // Add events container
    this.eventsContainer = document.createElement('div');
    this.overlay.appendChild(this.eventsContainer);

    document.body.appendChild(this.overlay);
    console.log('📊 Performance diagnostics overlay initialized');
  }

  /**
   * Log an event with timestamp
   */
  logEvent(type, message, data = {}) {
    if (!this.isEnabled) return;

    const timestamp = performance.now();
    const event = {
      type,
      message,
      timestamp,
      data,
    };

    this.events.push(event);

    // Keep only last N events
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }

    this.render();
  }

  /**
   * Calculate time difference between events
   */
  getTimeDiff(fromType, toType) {
    const fromEvent = [...this.events].reverse().find((e) => e.type === fromType);
    const toEvent = [...this.events].reverse().find((e) => e.type === toType);

    if (fromEvent && toEvent) {
      return toEvent.timestamp - fromEvent.timestamp;
    }
    return null;
  }

  /**
   * Render events to overlay
   */
  render() {
    if (!this.eventsContainer) return;

    let html = '';

    // Show events in reverse chronological order (most recent first)
    const recentEvents = [...this.events].reverse();

    recentEvents.forEach((event, index) => {
      const color = this.getColorForType(event.type);
      const timeStr = event.timestamp.toFixed(2);

      let dataStr = '';
      if (Object.keys(event.data).length > 0) {
        dataStr =
          '<br/>' +
          Object.entries(event.data)
            .map(([key, value]) => `  ${key}: ${value}`)
            .join('<br/>');
      }

      // Calculate time since previous event
      let timeDiff = '';
      if (index < recentEvents.length - 1) {
        const prevEvent = recentEvents[index + 1];
        const diff = event.timestamp - prevEvent.timestamp;
        const diffColor = diff > 100 ? '#f00' : diff > 50 ? '#ff0' : '#0f0';
        timeDiff = `<span style="color: ${diffColor}"> (+${diff.toFixed(1)}ms)</span>`;
      }

      html += `<div style="color: ${color}; margin-bottom: 8px; border-bottom: 1px solid #333; padding-bottom: 5px;">
        <strong>${event.type}</strong> @ ${timeStr}ms${timeDiff}<br/>
        ${event.message}${dataStr}
      </div>`;
    });

    // Add summary
    const detectedEvent = recentEvents.find((e) => e.type === 'DETECTED');
    const switchStartEvent = recentEvents.find((e) => e.type === 'SWITCH_START');
    const switchCompleteEvent = recentEvents.find((e) => e.type === 'SWITCH_COMPLETE');

    if (detectedEvent && switchStartEvent) {
      const detectionDelay = switchStartEvent.timestamp - detectedEvent.timestamp;
      const delayColor = detectionDelay > 50 ? '#f00' : detectionDelay > 20 ? '#ff0' : '#0f0';

      html =
        `<div style="color: #ff0; font-weight: bold; margin-bottom: 10px; padding: 10px; background: rgba(255,255,0,0.2); border: 1px solid #ff0;">
        🎯 Detection → Switch Delay: <span style="color: ${delayColor}; font-size: 16px;">${detectionDelay.toFixed(1)}ms</span>
      </div>` + html;
    }

    if (switchStartEvent && switchCompleteEvent) {
      const switchDuration = switchCompleteEvent.timestamp - switchStartEvent.timestamp;
      html =
        `<div style="color: #0ff; font-weight: bold; margin-bottom: 10px; padding: 10px; background: rgba(0,255,255,0.2); border: 1px solid #0ff;">
        🎬 Switch Duration: ${switchDuration.toFixed(1)}ms
      </div>` + html;
    }

    this.eventsContainer.innerHTML = html;
  }

  /**
   * Get color for event type
   */
  getColorForType(type) {
    const colors = {
      DETECTED: '#0f0',
      LOST: '#f00',
      SWITCH_START: '#0ff',
      SWITCH_COMPLETE: '#0f0',
      SELECTION: '#ff0',
      INFO: '#fff',
    };
    return colors[type] || '#aaa';
  }

  /**
   * Clear all events
   */
  clear() {
    this.events = [];
    this.render();
  }

  /**
   * Save diagnostics to server
   */
  async saveToServer() {
    const data = {
      exportTime: new Date().toISOString(),
      platform: navigator.userAgent,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      events: this.events.map((e) => ({
        type: e.type,
        message: e.message,
        timestamp: e.timestamp,
        data: e.data,
      })),
      summary: this.calculateSummary(),
    };

    try {
      console.log('📤 Saving diagnostics to server...');

      const response = await fetch('/api/diagnostics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Diagnostics saved to server:', result.filename);
        alert(`診断ログ保存完了！\nファイル名: ${result.filename}`);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Failed to save diagnostics to server:', error);
      alert(`サーバー保存失敗: ${error.message}\nブラウザダウンロードに切り替えます`);
      this.exportToFile();
    }
  }

  /**
   * Export events to JSON file for download (fallback)
   */
  exportToFile() {
    const data = {
      exportTime: new Date().toISOString(),
      platform: navigator.userAgent,
      events: this.events.map((e) => ({
        type: e.type,
        message: e.message,
        timestamp: e.timestamp,
        data: e.data,
      })),
      summary: this.calculateSummary(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-diagnostics-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('📥 Performance diagnostics downloaded');
  }

  /**
   * Calculate summary statistics
   */
  calculateSummary() {
    const summary = {
      totalEvents: this.events.length,
      eventCounts: {},
      delays: {},
    };

    // Count events by type
    this.events.forEach((e) => {
      summary.eventCounts[e.type] = (summary.eventCounts[e.type] || 0) + 1;
    });

    // Calculate average delays
    const detectedEvents = this.events.filter((e) => e.type === 'DETECTED');
    const switchStartEvents = this.events.filter((e) => e.type === 'SWITCH_START');
    const switchCompleteEvents = this.events.filter((e) => e.type === 'SWITCH_COMPLETE');

    if (detectedEvents.length > 0 && switchStartEvents.length > 0) {
      const delays = [];
      detectedEvents.forEach((detected) => {
        const nextSwitch = switchStartEvents.find((sw) => sw.timestamp > detected.timestamp);
        if (nextSwitch) {
          delays.push(nextSwitch.timestamp - detected.timestamp);
        }
      });
      if (delays.length > 0) {
        summary.delays.detectionToSwitch = {
          avg: delays.reduce((a, b) => a + b, 0) / delays.length,
          min: Math.min(...delays),
          max: Math.max(...delays),
          count: delays.length,
        };
      }
    }

    if (switchStartEvents.length > 0 && switchCompleteEvents.length > 0) {
      const durations = [];
      switchStartEvents.forEach((start) => {
        const complete = switchCompleteEvents.find(
          (c) => c.timestamp > start.timestamp && c.timestamp - start.timestamp < 100
        );
        if (complete) {
          durations.push(complete.timestamp - start.timestamp);
        }
      });
      if (durations.length > 0) {
        summary.delays.switchDuration = {
          avg: durations.reduce((a, b) => a + b, 0) / durations.length,
          min: Math.min(...durations),
          max: Math.max(...durations),
          count: durations.length,
        };
      }
    }

    return summary;
  }

  /**
   * Hide overlay
   */
  hide() {
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }
    this.isEnabled = false;
  }

  /**
   * Show overlay
   */
  show() {
    if (this.overlay) {
      this.overlay.style.display = 'block';
    } else {
      this.init();
    }
    this.isEnabled = true;
  }
}

// Global singleton instance
let diagnosticsInstance = null;

export function getDiagnostics() {
  if (!diagnosticsInstance) {
    diagnosticsInstance = new PerformanceDiagnostics();
  }
  return diagnosticsInstance;
}
